/**
 * Runtime application configuration.
 *
 * The admin SPA is deployed as static assets; environment-specific values are
 * injected at deploy time via a `/config.json` file served from the app's own
 * origin (see `public/config.example.json`). We fetch and validate it before
 * rendering so the authentication provider can be configured with real values.
 */

export interface CognitoConfig {
  userPoolId: string;
  clientId: string;
}

export interface AppConfig {
  apiBaseUrl: string;
  cognito: CognitoConfig;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function requireString(source: Record<string, unknown>, key: string, path: string): string {
  const value = source[key];
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`config.json: "${path}" must be a non-empty string`);
  }
  return value;
}

function parseConfig(raw: unknown): AppConfig {
  if (!isRecord(raw)) {
    throw new Error("config.json: expected a JSON object");
  }
  const apiBaseUrl = requireString(raw, "apiBaseUrl", "apiBaseUrl");

  const cognitoRaw = raw.cognito;
  if (!isRecord(cognitoRaw)) {
    throw new Error('config.json: "cognito" must be an object');
  }

  const cognito: CognitoConfig = {
    userPoolId: requireString(cognitoRaw, "userPoolId", "cognito.userPoolId"),
    clientId: requireString(cognitoRaw, "clientId", "cognito.clientId"),
  };

  return { apiBaseUrl, cognito };
}

/** Fetch and validate `/config.json` from the app origin. */
export async function loadConfig(): Promise<AppConfig> {
  let response: Response;
  try {
    response = await fetch("/config.json", { cache: "no-store" });
  } catch (cause) {
    throw new Error("Could not fetch /config.json — is the app deployed with a config file?", {
      cause,
    });
  }
  if (!response.ok) {
    throw new Error(`Could not load /config.json (HTTP ${response.status})`);
  }
  let json: unknown;
  try {
    json = (await response.json()) as unknown;
  } catch (cause) {
    throw new Error("config.json is not valid JSON", { cause });
  }
  return parseConfig(json);
}
